import { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { 
  Lock, 
  Users, 
  FileText, 
  MessageSquare, 
  Download,
  RefreshCw,
  LogOut,
  Phone,
  Mail,
  MapPin,
  Calendar,
  DollarSign,
  Building,
  Trash2
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_OPTIONS = [
  { value: 'new', label: 'New', color: 'bg-blue-100 text-blue-800' },
  { value: 'contacted', label: 'Contacted', color: 'bg-yellow-100 text-yellow-800' },
  { value: 'application_submitted', label: 'Application Submitted', color: 'bg-green-100 text-green-800' },
  { value: 'follow_up_required', label: 'Follow Up Required', color: 'bg-red-100 text-red-800' },
];

const getStatusStyle = (status) => {
  const found = STATUS_OPTIONS.find(s => s.value === status);
  return found ? found.color : 'bg-gray-100 text-gray-800';
};

const getStatusLabel = (status) => {
  const found = STATUS_OPTIONS.find(s => s.value === status);
  return found ? found.label : status || 'New';
};

export default function AdminPage() {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [password, setPassword] = useState('');
  const [storedPassword, setStoredPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('leads');
  
  const [stats, setStats] = useState(null);
  const [leads, setLeads] = useState([]);
  const [applications, setApplications] = useState([]);
  const [contacts, setContacts] = useState([]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API}/admin/login`, { password });
      if (response.data.success) {
        setIsLoggedIn(true);
        setStoredPassword(password);
        toast.success('Login successful');
        fetchData(password);
      }
    } catch (error) {
      toast.error('Invalid password');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    setIsLoggedIn(false);
    setPassword('');
    setStoredPassword('');
    setLeads([]);
    setApplications([]);
    setContacts([]);
    setStats(null);
  };

  const fetchData = async (pwd) => {
    setLoading(true);
    try {
      const [statsRes, leadsRes, appsRes, contactsRes] = await Promise.all([
        axios.get(`${API}/admin/stats?password=${pwd}`),
        axios.get(`${API}/admin/leads?password=${pwd}`),
        axios.get(`${API}/admin/applications?password=${pwd}`),
        axios.get(`${API}/admin/contacts?password=${pwd}`)
      ]);
      setStats(statsRes.data);
      setLeads(leadsRes.data);
      setApplications(appsRes.data);
      setContacts(contactsRes.data);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (type, id, newStatus) => {
    try {
      await axios.put(`${API}/admin/${type}/${id}/status?password=${storedPassword}`, { status: newStatus });
      toast.success('Status updated');
      fetchData(storedPassword);
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const deleteItem = async (type, id) => {
    if (!window.confirm('Are you sure you want to delete this item? This action cannot be undone.')) {
      return;
    }
    try {
      await axios.delete(`${API}/admin/${type}/${id}?password=${storedPassword}`);
      toast.success('Deleted successfully');
      fetchData(storedPassword);
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const downloadFile = (appId, filename) => {
    window.open(`${API}/admin/files/${appId}/${filename}?password=${storedPassword}`, '_blank');
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (!isLoggedIn) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-2xl p-8 w-full max-w-md">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-slate-900 rounded-full flex items-center justify-center mx-auto mb-4">
              <Lock className="w-8 h-8 text-amber-500" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900">Admin Dashboard</h1>
            <p className="text-slate-500 mt-2">Enter password to access</p>
          </div>
          <form onSubmit={handleLogin}>
            <Input
              type="password"
              placeholder="Enter admin password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="mb-4"
              data-testid="admin-password-input"
            />
            <Button 
              type="submit" 
              className="w-full bg-slate-900 hover:bg-slate-800"
              disabled={loading}
              data-testid="admin-login-btn"
            >
              {loading ? 'Verifying...' : 'Login'}
            </Button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100">
      {/* Header */}
      <header className="bg-slate-900 text-white py-4 px-6 shadow-lg">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div>
            <h1 className="text-xl font-bold">GANN PRIVATE LENDING</h1>
            <p className="text-slate-400 text-sm">Admin Dashboard</p>
          </div>
          <div className="flex items-center gap-4">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => fetchData(storedPassword)}
              className="text-slate-900"
              data-testid="refresh-btn"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
            <Button 
              variant="destructive" 
              size="sm" 
              onClick={handleLogout}
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto p-6">
        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-500 text-sm">Total Leads</p>
                  <p className="text-3xl font-bold text-slate-900">{stats.total_leads}</p>
                  {stats.new_leads > 0 && (
                    <p className="text-sm text-blue-600 mt-1">{stats.new_leads} new</p>
                  )}
                </div>
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                  <Users className="w-6 h-6 text-blue-600" />
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-500 text-sm">Applications</p>
                  <p className="text-3xl font-bold text-slate-900">{stats.total_applications}</p>
                  {stats.new_applications > 0 && (
                    <p className="text-sm text-green-600 mt-1">{stats.new_applications} new</p>
                  )}
                </div>
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                  <FileText className="w-6 h-6 text-green-600" />
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-500 text-sm">Messages</p>
                  <p className="text-3xl font-bold text-slate-900">{stats.total_contacts}</p>
                  {stats.new_contacts > 0 && (
                    <p className="text-sm text-amber-600 mt-1">{stats.new_contacts} new</p>
                  )}
                </div>
                <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center">
                  <MessageSquare className="w-6 h-6 text-amber-600" />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
          <div className="flex border-b border-slate-200">
            <button
              onClick={() => setActiveTab('leads')}
              className={`flex-1 py-4 px-6 text-center font-medium transition-colors ${
                activeTab === 'leads' 
                  ? 'bg-slate-900 text-white' 
                  : 'text-slate-600 hover:bg-slate-50'
              }`}
              data-testid="tab-leads"
            >
              <Users className="w-5 h-5 inline-block mr-2" />
              Leads ({leads.length})
            </button>
            <button
              onClick={() => setActiveTab('applications')}
              className={`flex-1 py-4 px-6 text-center font-medium transition-colors ${
                activeTab === 'applications' 
                  ? 'bg-slate-900 text-white' 
                  : 'text-slate-600 hover:bg-slate-50'
              }`}
              data-testid="tab-applications"
            >
              <FileText className="w-5 h-5 inline-block mr-2" />
              Applications ({applications.length})
            </button>
            <button
              onClick={() => setActiveTab('contacts')}
              className={`flex-1 py-4 px-6 text-center font-medium transition-colors ${
                activeTab === 'contacts' 
                  ? 'bg-slate-900 text-white' 
                  : 'text-slate-600 hover:bg-slate-50'
              }`}
              data-testid="tab-contacts"
            >
              <MessageSquare className="w-5 h-5 inline-block mr-2" />
              Messages ({contacts.length})
            </button>
          </div>

          {/* Content */}
          <div className="p-6">
            {loading ? (
              <div className="text-center py-12">
                <RefreshCw className="w-8 h-8 animate-spin mx-auto text-slate-400" />
                <p className="text-slate-500 mt-4">Loading...</p>
              </div>
            ) : (
              <>
                {/* Leads Tab */}
                {activeTab === 'leads' && (
                  <div className="space-y-4">
                    {leads.length === 0 ? (
                      <p className="text-center text-slate-500 py-12">No leads yet</p>
                    ) : (
                      leads.map((lead) => (
                        <div key={lead.id} className="border border-slate-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                          <div className="flex flex-wrap justify-between items-start gap-4">
                            <div className="flex-1 min-w-[250px]">
                              <h3 className="font-semibold text-lg text-slate-900">{lead.name}</h3>
                              <div className="flex flex-wrap gap-4 mt-2 text-sm text-slate-600">
                                <span className="flex items-center gap-1">
                                  <Mail className="w-4 h-4" /> {lead.email}
                                </span>
                                <span className="flex items-center gap-1">
                                  <Phone className="w-4 h-4" /> {lead.phone}
                                </span>
                              </div>
                              {lead.property_address && (
                                <p className="flex items-center gap-1 mt-2 text-sm text-slate-600">
                                  <MapPin className="w-4 h-4" /> {lead.property_address}
                                </p>
                              )}
                              {(lead.purchase_price || lead.loan_amount) && (
                                <p className="flex items-center gap-1 mt-1 text-sm text-slate-600">
                                  <DollarSign className="w-4 h-4" /> 
                                  {lead.purchase_price && `Purchase: $${Number(lead.purchase_price).toLocaleString()}`}
                                  {lead.purchase_price && lead.loan_amount && ' | '}
                                  {lead.loan_amount && `Loan: $${Number(lead.loan_amount).toLocaleString()}`}
                                </p>
                              )}
                              {lead.message && (
                                <p className="mt-2 text-sm text-slate-500 italic">"{lead.message}"</p>
                              )}
                              <p className="flex items-center gap-1 mt-2 text-xs text-slate-400">
                                <Calendar className="w-3 h-3" /> {formatDate(lead.created_at)}
                              </p>
                            </div>
                            <div className="flex items-center gap-3">
                              <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusStyle(lead.status)}`}>
                                {getStatusLabel(lead.status)}
                              </span>
                              <Select 
                                value={lead.status || 'new'} 
                                onValueChange={(value) => updateStatus('leads', lead.id, value)}
                              >
                                <SelectTrigger className="w-[180px]">
                                  <SelectValue placeholder="Update status" />
                                </SelectTrigger>
                                <SelectContent>
                                  {STATUS_OPTIONS.map((opt) => (
                                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              <Button 
                                variant="destructive" 
                                size="sm"
                                onClick={() => deleteItem('leads', lead.id)}
                                data-testid={`delete-lead-${lead.id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                )}

                {/* Applications Tab */}
                {activeTab === 'applications' && (
                  <div className="space-y-4">
                    {applications.length === 0 ? (
                      <p className="text-center text-slate-500 py-12">No applications yet</p>
                    ) : (
                      applications.map((app) => (
                        <div key={app.id} className="border border-slate-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                          <div className="flex flex-wrap justify-between items-start gap-4">
                            <div className="flex-1 min-w-[300px]">
                              <h3 className="font-semibold text-lg text-slate-900">{app.name}</h3>
                              {app.entity_name && (
                                <p className="flex items-center gap-1 text-sm text-slate-600">
                                  <Building className="w-4 h-4" /> {app.entity_name} ({app.entity_type})
                                </p>
                              )}
                              <div className="flex flex-wrap gap-4 mt-2 text-sm text-slate-600">
                                <span className="flex items-center gap-1">
                                  <Mail className="w-4 h-4" /> {app.email}
                                </span>
                                <span className="flex items-center gap-1">
                                  <Phone className="w-4 h-4" /> {app.phone}
                                </span>
                              </div>
                              {app.property_address && (
                                <p className="flex items-center gap-1 mt-2 text-sm text-slate-600">
                                  <MapPin className="w-4 h-4" /> {app.property_address}
                                  {app.property_city && `, ${app.property_city}`}
                                  {app.property_state && `, ${app.property_state}`}
                                  {app.property_zip && ` ${app.property_zip}`}
                                </p>
                              )}
                              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-3 text-sm">
                                {app.purchase_price && (
                                  <div className="bg-slate-50 p-2 rounded">
                                    <span className="text-slate-500 text-xs">Purchase Price</span>
                                    <p className="font-medium">${app.purchase_price}</p>
                                  </div>
                                )}
                                {app.loan_amount && (
                                  <div className="bg-slate-50 p-2 rounded">
                                    <span className="text-slate-500 text-xs">Loan Amount</span>
                                    <p className="font-medium">${app.loan_amount}</p>
                                  </div>
                                )}
                                {app.loan_type && (
                                  <div className="bg-slate-50 p-2 rounded">
                                    <span className="text-slate-500 text-xs">Loan Type</span>
                                    <p className="font-medium capitalize">{app.loan_type.replace('_', ' ')}</p>
                                  </div>
                                )}
                                {app.credit_score_range && (
                                  <div className="bg-slate-50 p-2 rounded">
                                    <span className="text-slate-500 text-xs">Credit Score</span>
                                    <p className="font-medium">{app.credit_score_range}</p>
                                  </div>
                                )}
                              </div>
                              {app.exit_plan && (
                                <p className="mt-2 text-sm text-slate-600"><strong>Exit Plan:</strong> {app.exit_plan}</p>
                              )}
                              {app.additional_notes && (
                                <p className="mt-2 text-sm text-slate-500 italic">"{app.additional_notes}"</p>
                              )}
                              {/* File Downloads */}
                              {app.files_uploaded && app.files_uploaded.length > 0 && (
                                <div className="mt-3">
                                  <p className="text-sm font-medium text-slate-700 mb-2">Attachments:</p>
                                  <div className="flex flex-wrap gap-2">
                                    {app.files_uploaded.map((filename, idx) => (
                                      <Button
                                        key={idx}
                                        variant="outline"
                                        size="sm"
                                        onClick={() => downloadFile(app.id, filename)}
                                        className="text-xs"
                                      >
                                        <Download className="w-3 h-3 mr-1" />
                                        {filename}
                                      </Button>
                                    ))}
                                  </div>
                                </div>
                              )}
                              <p className="flex items-center gap-1 mt-3 text-xs text-slate-400">
                                <Calendar className="w-3 h-3" /> {formatDate(app.created_at)}
                              </p>
                            </div>
                            <div className="flex items-center gap-3">
                              <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusStyle(app.status)}`}>
                                {getStatusLabel(app.status)}
                              </span>
                              <Select 
                                value={app.status || 'new'} 
                                onValueChange={(value) => updateStatus('applications', app.id, value)}
                              >
                                <SelectTrigger className="w-[180px]">
                                  <SelectValue placeholder="Update status" />
                                </SelectTrigger>
                                <SelectContent>
                                  {STATUS_OPTIONS.map((opt) => (
                                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              <Button 
                                variant="destructive" 
                                size="sm"
                                onClick={() => deleteItem('applications', app.id)}
                                data-testid={`delete-app-${app.id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                )}

                {/* Contacts Tab */}
                {activeTab === 'contacts' && (
                  <div className="space-y-4">
                    {contacts.length === 0 ? (
                      <p className="text-center text-slate-500 py-12">No messages yet</p>
                    ) : (
                      contacts.map((contact) => (
                        <div key={contact.id} className="border border-slate-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                          <div className="flex flex-wrap justify-between items-start gap-4">
                            <div className="flex-1 min-w-[250px]">
                              <h3 className="font-semibold text-lg text-slate-900">{contact.name}</h3>
                              <div className="flex flex-wrap gap-4 mt-2 text-sm text-slate-600">
                                <span className="flex items-center gap-1">
                                  <Mail className="w-4 h-4" /> {contact.email}
                                </span>
                                {contact.phone && (
                                  <span className="flex items-center gap-1">
                                    <Phone className="w-4 h-4" /> {contact.phone}
                                  </span>
                                )}
                              </div>
                              <p className="mt-3 text-slate-700 bg-slate-50 p-3 rounded-lg">{contact.message}</p>
                              <p className="flex items-center gap-1 mt-3 text-xs text-slate-400">
                                <Calendar className="w-3 h-3" /> {formatDate(contact.created_at)}
                              </p>
                            </div>
                            <div className="flex items-center gap-3">
                              <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusStyle(contact.status)}`}>
                                {getStatusLabel(contact.status)}
                              </span>
                              <Select 
                                value={contact.status || 'new'} 
                                onValueChange={(value) => updateStatus('contacts', contact.id, value)}
                              >
                                <SelectTrigger className="w-[180px]">
                                  <SelectValue placeholder="Update status" />
                                </SelectTrigger>
                                <SelectContent>
                                  {STATUS_OPTIONS.map((opt) => (
                                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              <Button 
                                variant="destructive" 
                                size="sm"
                                onClick={() => deleteItem('contacts', contact.id)}
                                data-testid={`delete-contact-${contact.id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
